/**
 * CalendarioPage — Vista mensual del calendario de salidas.
 * Tarjetas por día, con indicador de cupos y estado climático.
 * Click en día → muestra salidas de ese día abajo.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, IconButton, Paper, Stack, Chip, LinearProgress, Button,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
function getEmpresaId() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; }
}
function authHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const DIAS_LETRAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function toIsoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function gridDays(year, month) {
  // Primer día del mes
  const first = new Date(year, month, 1);
  const dowFirst = first.getDay();
  // Empezar grilla en domingo anterior
  const start = new Date(year, month, 1 - dowFirst);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

export default function CalendarioPage() {
  const empresaId = getEmpresaId();
  const [mes, setMes] = useState(() => {
    const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [salidas, setSalidas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diaSel, setDiaSel] = useState(toIsoDate(new Date()));

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/salidas?empresa_id=${empresaId}`, { headers: authHeaders() });
      const data = await r.json();
      setSalidas(Array.isArray(data) ? data : (data.salidas || []));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Indexar salidas por fecha (YYYY-MM-DD)
  const porDia = useMemo(() => {
    const map = {};
    salidas.forEach(s => {
      if (!s.fecha) return;
      const k = s.fecha.split('T')[0];
      if (!map[k]) map[k] = [];
      map[k].push(s);
    });
    return map;
  }, [salidas]);

  const days = useMemo(() => gridDays(mes.year, mes.month), [mes]);
  const salidasDiaSel = porDia[diaSel] || [];

  const cambiarMes = (delta) => {
    const d = new Date(mes.year, mes.month + delta, 1);
    setMes({ year: d.getFullYear(), month: d.getMonth() });
  };

  const irHoy = () => {
    const d = new Date();
    setMes({ year: d.getFullYear(), month: d.getMonth() });
    setDiaSel(toIsoDate(d));
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Calendario de salidas</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Vista mensual con indicador de ocupación y clima por día.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton size="small" onClick={() => cambiarMes(-1)}><ChevronLeftIcon /></IconButton>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a', minWidth: 160, textAlign: 'center' }}>
            {MESES[mes.month]} {mes.year}
          </Typography>
          <IconButton size="small" onClick={() => cambiarMes(1)}><ChevronRightIcon /></IconButton>
          <Button size="small" startIcon={<TodayIcon />} onClick={irHoy} sx={{ textTransform: 'none' }}>Hoy</Button>
        </Stack>
      </Box>

      {/* Grid calendario */}
      <Paper sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
        {/* Encabezado días de la semana */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
          {DIAS_LETRAS.map(d => (
            <Typography key={d} sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textAlign: 'center', textTransform: 'uppercase' }}>
              {d}
            </Typography>
          ))}
        </Box>
        {/* Días */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {days.map((d, i) => {
            const iso = toIsoDate(d);
            const enMes = d.getMonth() === mes.month;
            const esHoy = iso === toIsoDate(new Date());
            const esSel = iso === diaSel;
            const list = porDia[iso] || [];
            const numSalidas = list.length;
            const totalCap = list.reduce((acc, s) => acc + (s.capacidad_total || 0), 0);
            const totalRes = list.reduce((acc, s) => acc + (s.asientos_reservados || 0), 0);
            const pct = totalCap > 0 ? (totalRes / totalCap) * 100 : 0;
            const tieneClimaAlerta = list.some(s => s.estado_clima && s.estado_clima !== 'normal');

            return (
              <Box key={i} onClick={() => setDiaSel(iso)}
                sx={{
                  minHeight: 70, p: 0.5, borderRadius: 1.5, cursor: 'pointer',
                  bgcolor: esSel ? '#0d948815' : enMes ? '#fff' : '#f8fafc',
                  border: '1.5px solid', borderColor: esSel ? '#0d9488' : (esHoy ? '#fbbf24' : 'transparent'),
                  opacity: enMes ? 1 : 0.5,
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: '#f0fdfa', borderColor: '#14b8a6' },
                }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{
                    fontSize: 11, fontWeight: esHoy ? 800 : 600,
                    color: esHoy ? '#92400e' : enMes ? '#0f172a' : '#94a3b8',
                  }}>{d.getDate()}</Typography>
                  {tieneClimaAlerta && <span style={{ fontSize: 10 }}>⚠</span>}
                </Box>
                {numSalidas > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip size="small" label={`${numSalidas} salida${numSalidas > 1 ? 's' : ''}`}
                      sx={{ height: 16, fontSize: 9, fontWeight: 700,
                             bgcolor: '#dbeafe', color: '#1e3a8a' }} />
                    {totalCap > 0 && (
                      <LinearProgress variant="determinate" value={Math.min(pct, 100)}
                        sx={{ height: 3, borderRadius: 2, mt: 0.5,
                               bgcolor: '#f1f5f9',
                               '& .MuiLinearProgress-bar': {
                                 bgcolor: pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : '#16a34a',
                               } }} />
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Salidas del día seleccionado */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#475569', mb: 1 }}>
          Salidas del {new Date(diaSel + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
        </Typography>
        {salidasDiaSel.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2, color: '#94a3b8' }}>
            <Typography sx={{ fontSize: 13 }}>Sin salidas programadas este día.</Typography>
          </Paper>
        ) : (
          <Stack spacing={1}>
            {salidasDiaSel.map(s => {
              const pct = (s.capacidad_total || 0) > 0
                ? ((s.asientos_reservados || 0) / s.capacidad_total) * 100 : 0;
              const color = pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : '#16a34a';
              return (
                <Paper key={s.id} sx={{ p: 1.5, borderRadius: 2, borderLeft: `4px solid ${color}` }}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                        {s.hora_salida?.slice(0, 5) || '—'} · {s.paquete_nombre}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                        {s.punto_embarque_nombre || 'Sin punto'} · {(s.botes || []).length} bote(s)
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color }}>
                        {s.asientos_reservados || 0}/{s.capacidad_total || 0}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>{pct.toFixed(0)}% ocupado</Typography>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
